import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Sun, Battery, Zap, Search, Filter, Plus, MoreHorizontal,
  Eye, Edit, Trash2, Building2, ExternalLink
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { installationAPI, propertyAPI } from '../lib/api';
import { getInstallationTypeBadgeClass, getInstallationTypeLabel, formatDate, getGoogleMapsUrl } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Installations = () => {
  const [installations, setInstallations] = useState([]);
  const [properties, setProperties] = useState({});
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [olderThanFilter, setOlderThanFilter] = useState(null);
  const [searchParams] = useSearchParams();
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const initialType = searchParams.get('type');

  useEffect(() => {
    if (initialType) {
      setTypeFilter(initialType);
    }
  }, [initialType]);

  const fetchData = async () => {
    try {
      const params = {};
      if (typeFilter !== 'all') params.installation_type = typeFilter;
      if (olderThanFilter) params.older_than_years = olderThanFilter;

      const [instRes, propsRes] = await Promise.all([
        installationAPI.getAll(params),
        propertyAPI.getAll()
      ]);
      
      setInstallations(instRes.data);
      
      // Create property lookup
      const propMap = {};
      propsRes.data.forEach(p => { propMap[p.id] = p; });
      setProperties(propMap);
    } catch (error) {
      toast.error('Failed to load installations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [typeFilter, olderThanFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm('Deze installatie verwijderen?')) return;
    try {
      await installationAPI.delete(id);
      toast.success('Installation deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete installation');
    }
  };

  const getInstallationIcon = (type) => {
    switch (type) {
      case 'solar_panels': return <Sun className="w-5 h-5 text-amber-400" />;
      case 'battery': return <Battery className="w-5 h-5 text-blue-400" />;
      case 'ev_charger': return <Zap className="w-5 h-5 text-green-400" />;
      default: return <Sun className="w-5 h-5 text-purple-400" />;
    }
  };

  const getInstallationBgClass = (type) => {
    switch (type) {
      case 'solar_panels': return 'bg-amber-500/20';
      case 'battery': return 'bg-blue-500/20';
      case 'ev_charger': return 'bg-green-500/20';
      default: return 'bg-purple-500/20';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="installations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Installaties</h1>
          <p className="text-muted-foreground mt-1">All solar panels, batteries, and EV chargers</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48" data-testid="type-filter">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="solar_panels">Solar Panels</SelectItem>
            <SelectItem value="battery">Battery</SelectItem>
            <SelectItem value="ev_charger">EV Charger</SelectItem>
            <SelectItem value="inverter">Inverter</SelectItem>
          </SelectContent>
        </Select>

        <Select value={olderThanFilter?.toString() || 'none'} onValueChange={(v) => setOlderThanFilter(v === 'none' ? null : parseInt(v))}>
          <SelectTrigger className="w-48" data-testid="age-filter">
            <SelectValue placeholder="Filter by age" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Any Age</SelectItem>
            <SelectItem value="3">Older than 3 years</SelectItem>
            <SelectItem value="5">Older than 5 years</SelectItem>
            <SelectItem value="10">Older than 10 years</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Installation List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="h-40 animate-pulse bg-muted" />
          ))}
        </div>
      ) : installations.length === 0 ? (
        <Card className="p-12 text-center">
          <Sun className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-lg font-medium text-muted-foreground">Geen installaties gevonden</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {installations.map((inst) => {
            const property = properties[inst.property_id];
            return (
              <Card 
                key={inst.id} 
                className="card-hover cursor-pointer"
                onClick={() => navigate(`/properties/${inst.property_id}`)}
                data-testid={`installation-card-${inst.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${getInstallationBgClass(inst.installation_type)}`}>
                        {getInstallationIcon(inst.installation_type)}
                      </div>
                      <div>
                        <p className="font-medium">{getInstallationTypeLabel(inst.installation_type)}</p>
                        {inst.installation_year && (
                          <p className="text-xs text-muted-foreground">Installed {inst.installation_year}</p>
                        )}
                      </div>
                    </div>
                    {hasRole('admin') && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/properties/${inst.property_id}`); }}>
                            <Eye className="w-4 h-4 mr-2" />
                            Bekijk Adres
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); handleDelete(inst.id); }}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Verwijderen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-1 text-sm mb-3">
                    {inst.number_of_panels && <p><span className="text-muted-foreground">Panelen:</span> {inst.number_of_panels}</p>}
                    {inst.panel_brand && <p><span className="text-muted-foreground">Merk:</span> {inst.panel_brand}</p>}
                    {inst.battery_capacity && <p><span className="text-muted-foreground">Capaciteit:</span> {inst.battery_capacity} kWh</p>}
                    {inst.charger_power && <p><span className="text-muted-foreground">Vermogen:</span> {inst.charger_power} kW</p>}
                  </div>

                  {/* Property */}
                  {property && (
                    <div className="flex items-center gap-2 text-xs pt-3 border-t border-border/50">
                      <span 
                        className="flex items-center gap-1 text-primary cursor-pointer hover:underline"
                        onClick={(e) => { e.stopPropagation(); navigate(`/properties/${inst.property_id}`); }}
                      >
                        <Building2 className="w-3 h-3" />
                        {property.street} {property.number}, {property.city}
                      </span>
                      <a
                        href={getGoogleMapsUrl(property)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-muted-foreground hover:text-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Installations;
